import { IIsoDocuments } from 'typescript/interfaces/iso'
import { IPagination } from 'typescript/interfaces/pagination'
import { IPortfolioSummarySortFields } from 'typescript/enums/syndicators'
import { IUserPage } from 'typescript/interfaces/users'
import { DealPaidStatus, IExtendType, IScheduledPaymentInfo } from 'typescript/interfaces/deals'
import { IValidateBankRouting } from 'hooks/useValidateBankRouting'
import { CompanyType, IState } from './companies'
import { IExportParams } from './export'

export enum SyndicatorSortFields {
  Id = 'Id',
  Name = 'Name',
  DataJoined = 'DataJoined',
  Deals = 'Deals',
  AmountSyndicated = 'AmountSyndicated',
  StartingBalance = 'StartingBalance',
  AvailableBalance = 'AvailableBalance',
  LastTransactions = 'LastTransactions',
}

export interface SyndicatorForm {
  id?: number
  firstName: string
  lastName: string
  phone: string
  fax: string
  mobile: string
  email: string
  nameOnCheck: string
  userPage: IUserPage & IValidateBankRouting
  label: string
  businessName: string
  city: string
  street: string
  zipCode: string
  federalTaxId: string
  stateId: number | string
  managementFee: string | number
  getsBankFee: boolean
}

export interface SyndicatorListItem {
  id: number
  firstName: string
  lastName: string
  phone: string
  fax: string
  mobile: string
  startingBalance: string
  email: string
  deals: number
  businessName: string
  city: string
  street: string
  zipCode: string
  federalTaxId: string
  state: IState
  dataJoined: string
  availableBalance: number
  amountSyndicated: number
  lastTransaction: string
  managementFee: string | number
}

export interface ILightSyndicator {
  date: string
  id: number
  name: string
}

export interface ISyndicatorWithBalance extends ILightSyndicator {
  availableBalance: number
}

export interface ISyndicatorDetailEntity {
  id: number
  firstName: string
  lastName: string
  phone: string
  fax: string
  mobile: string
  email: string
  deals: number
  dataJoined: string
  availableBalance: number
  amountSyndicated: number
  lastTransaction: string
  nameOnCheck: string
  documents: IIsoDocuments[]
  userPage: IUserPage
  label: string
  businessName: string
  city: string
  street: string
  zipCode: string
  federalTaxId: string
  state: IState
  managementFee: string | number
  getsBankFee: boolean
}

export interface ISyndicatorExportParams extends IExportParams {
  Criteria: IGetSyndicatorParams
}

export interface ISyndicatorExportSummaryParams extends IExportParams {
  Criteria: IPortfolioSummaryParams
}

export interface ISyndicatorReportExportParams extends IExportParams {
  Criteria: IPagination<SyndicatorDealsSortingFields> & { filter: ISyndicatorReportFilters }
}

export interface ISyndicatorReportResponse {
  name: string
  totalDealAdvance: number
  totalSyndicated: number
  totalProjPayback: number
  totalToDatePayback: number
  totalDealCollected: number
  totalCollected: number
  totalDaily: number
  totalWeekly: number
  totalRemaining: number
  totalDealRemaining: number
  totalPnl: number
  totalDealPnl: number
}

export interface ISyndicatorReportListItem {
  merchant: string
  deal: number
  extendType: IExtendType | null
  dealEndDate: string
  term: number
  factor: number
  participation: number
  dealAdvance: number
  syndicated: number
  projPayback: number
  toDatePayback: number
  collected: number
  dealCollected: number
  remainingBalance: number
  dealRemaining: number
  startDate: string
  status: string
  totalCommission: number
  legalFee: number
  mgmFee: number
  defaultFee: number
  wireFee: number
  bounceFee: number
  bankFee: number
  pnl: number
  initialScheduledPaymentInfo: IScheduledPaymentInfo
  activeScheduledPaymentInfo: IScheduledPaymentInfo
  totalDealFunding: number
  totalDealCommission: number
  totalDealLegalFee: number
  totalDealPsfFee: number
  totalDealSyndicationFee: number
  totalDealDefaultFee: number
  totalDealWireFee: number
  totalDealBounceFee: number
  totalDealPnl: number
  totalDealRemaining: number
  totalPercentageCollected: number
  tableOnlyRowExpanded?: boolean
}

export interface ISyndicatorLight {
  id: number
  name: string
}

export interface IConnectSyndicatorFileLinksToIsoParams {
  documentUrl: string
  syndicatorId: number | string
  description: string
}

export enum SyndicatorDealsSortingFields {
  merchant = 'BusinessName',
  deal = 'Id',
  dealEndDate = 'dealEndDate',
  participation = 'participation',
  dealAdvance = 'Advanced',
  syndicated = 'syndicated',
  projPayback = 'projPayback',
  toDatePayback = 'Payback',
  dealStartDate = 'funded',
  syndicatorRemaining = 'syndicatorRemaining',
  term = 'Term',
}

type StatusPortfolioSummary = 'Draft' | 'Pending' | 'Declined' | 'Approved'

export interface IPortfolioSummaryItem {
  merchant: string
  company: string
  funded: string
  deal: number
  status: StatusPortfolioSummary
  paidStatus: DealPaidStatus
  createdDate: string
  term: number
  shares: number
  dealEndDate: string
  factorRate: number
  advanceAmount: number
  paybackAmount: number
  syndicationFee: number
  paidOff: number
  payOffAmount: number
  projectedRtr: number
  isCollectable: boolean
  initialScheduledPaymentInfo: IScheduledPaymentInfo
  activeScheduledPaymentInfo: IScheduledPaymentInfo
}

export interface IPortfolioSummaryTotal {
  frequentPayment: number
  advanceAmount: number
  paybackAmount: number
  syndicationFee: number
  payOffAmount: number
  projectedRtr: number
  paidOff: number
}

export interface IPortfolioSummaryParams extends IPagination<IPortfolioSummarySortFields> {}

export interface ISyndicatedDealsParams extends IPagination<IPortfolioSummarySortFields> {
  SyndicatorId?: string
  ClientId?: string
}

export interface ISyndicatedDealStatsParams {
  SyndicatorId?: string
  DealId?: string
}

export interface ISyndicatedDealListItem {
  dealId: number
  businessName: string
  participation: number
  sharedInDeal: number
  purchased: number
  collected: number
  remainingBalance: number
  startDate: string
  status: string
}

export interface ISyndicatedDealStatsItem {
  month: number
  amount: number
}

export interface IGetSyndicatorParams extends IPagination<SyndicatorSortFields> {
  CompanyType?: CompanyType
}

export interface ISyndicatorReportFilters {
  SyndicatorId: string
  paidStatuses?: Array<DealPaidStatus>
}
